// Only presentation text enters this cache. IDs, playback data and source records stay unchanged.
export function createTranslationStore({ transport, scope = () => "", now = Date.now, schedule = setTimeout, cancel = clearTimeout, enabled = () => true }) {
  const entries = new Map(), listeners = new Set();
  let context, generation = 0, timer, running = false;
  const keyFor = (text, target) => JSON.stringify([target, text]);
  const sync = () => {
    const next = scope();
    if (next !== context) {
      context = next; generation++; entries.clear();
      if (timer) cancel(timer); timer = null;
    }
  };
  const eligible = (text, target) => enabled() && ["en", "ar"].includes(target) && typeof text === "string" &&
    text.length > 0 && text.length <= 6000 && /[a-zA-Z\u00c0-\u024f\u0600-\u06ff]/.test(text) &&
    !/^https?:\/\/\S+$/i.test(text) && !(target === "ar" && /[\u0600-\u06ff]/.test(text));
  const notify = keys => listeners.forEach(fn => fn(keys));
  const arm = (delay = 300) => { if (!timer && !running) timer = schedule(pump, delay); };
  const pump = async () => {
    timer = null; sync(); if (running) return;
    let available = 32 - [...entries.values()].filter(v => v.status === "pending" && v.issued).length;
    const due = [...entries.entries()].filter(([, v]) => v.status === "pending" && v.next <= now() && (v.issued || available-- > 0));
    if (!due.length) { if ([...entries.values()].some(v => v.status === "pending")) arm(500); return; }
    const batch = []; let chars = 0;
    for (const item of due) {
      if (item[1].target !== due[0][1].target || batch.length === 8 || chars + item[1].source.length > 16000) continue;
      chars += item[1].source.length; batch.push(item);
    }
    batch.forEach(([, entry]) => { if (!entry.issued) { entry.issued = true; entry.started = now(); } });
    const version = generation; running = true;
    try {
      const result = await transport({ texts: batch.map(([, v]) => v.source), target: batch[0][1].target });
      sync(); if (version !== generation) return;
      const changed = [];
      batch.forEach(([key, entry], index) => {
        const value = result?.translations?.[index];
        if (value?.source !== entry.source || !["ready", "pending", "unavailable"].includes(value.status)) throw new Error("Invalid translation response");
        if (value.status === "ready" && typeof value.text === "string" && value.text.trim()) {
          entry.text = value.text; entry.status = "ready"; changed.push(key);
        } else if (value.status === "pending" && now() - entry.started < 90000) {
          entry.next = now() + Math.max(1500, Math.min(result.retryAfterMs || 1500, 5000));
        } else { entry.status = "unavailable"; entry.next = now() + 30000; }
      });
      notify(changed);
    } catch (_) {
      sync(); if (version === generation) batch.forEach(([, entry]) => { entry.status = "unavailable"; entry.next = now() + 30000; });
    } finally { running = false; arm(); }
  };
  return {
    keyFor,
    read(text, target) { sync(); return eligible(text, target) ? entries.get(keyFor(text, target))?.text || text : text; },
    request(text, target) {
      sync(); if (!eligible(text, target)) return;
      const key = keyFor(text, target), old = entries.get(key);
      if (old && (old.status !== "unavailable" || old.next > now())) return;
      entries.delete(key); entries.set(key, { source: text, target, text, status: "pending", started: now(), next: now() });
      while (entries.size > 5000) entries.delete(entries.keys().next().value);
      arm();
    },
    subscribe(fn) { listeners.add(fn); return () => listeners.delete(fn); },
    clear() { generation++; entries.clear(); if (timer) cancel(timer); timer = null; },
    dispose() { this.clear(); listeners.clear(); }
  };
}
